import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { DeleteOutline, Block, Add, Visibility } from "@material-ui/icons";
import { format, isValid, parseISO } from "date-fns";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ScheduledBroadcastModal from "../../components/ScheduledBroadcastModal";
import ScheduledBroadcastDetailsModal from "../../components/ScheduledBroadcastDetailsModal";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  colorIcon: {
    color: "#0035a2"
  }
}));

const ScheduledBroadcasts = () => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [queues, setQueues] = useState([]);
  const [tags, setTags] = useState([]);
  const [scheduledBroadcasts, setScheduledBroadcasts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsCampaign, setDetailsCampaign] = useState(null);
  const [detailsRecipients, setDetailsRecipients] = useState([]);

  const formatDateTime = value => {
    if (!value) return "-";
    const date = parseISO(value);
    if (!isValid(date)) return "-";
    return format(date, "dd/MM/yyyy HH:mm");
  };

  const formatCampaignStatus = status => {
    const labels = {
      pending: "Pendente",
      processing: "Processando",
      completed: "Concluído",
      completed_with_errors: "Concluído com erros",
      cancelled: "Cancelado",
      failed: "Falhou"
    };

    return labels[String(status || "").toLowerCase()] || status || "-";
  };

  const parseIds = value => {
    try {
      const parsed = JSON.parse(value || "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed.map(item => Number(item)).filter(item => Number.isInteger(item) && item > 0);
    } catch (_error) {
      return [];
    }
  };

  const formatWhatsapps = item => {
    const ids = parseIds(item?.whatsappIds);
    if (!ids.length) {
      return item.whatsapp?.name || `#${item.whatsappId}`;
    }

    const names = ids.map(id => {
      const found = (whatsApps || []).find(w => Number(w.id) === id);
      return found?.name || `#${id}`;
    });

    return names.join(", ");
  };

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      let page = 1;
      let hasMore = true;
      const loaded = [];

      while (hasMore && page <= 25) {
        const { data } = await api.get("/contacts", {
          params: {
            pageNumber: page,
            searchParam: ""
          }
        });

        loaded.push(...(data?.contacts || []));
        hasMore = Boolean(data?.hasMore);
        page += 1;
      }

      setContacts(loaded);
    } catch (err) {
      toastError(err);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  const loadTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const { data } = await api.get("/tags");
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setTagsLoading(false);
    }
  }, []);

  const loadQueues = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      setQueues(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
      setQueues([]);
    }
  }, []);

  const loadScheduledBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduled-broadcasts");
      setScheduledBroadcasts(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
    loadTags();
    loadQueues();
    loadScheduledBroadcasts();
  }, [loadContacts, loadTags, loadQueues, loadScheduledBroadcasts]);

  const handleCreateScheduledBroadcast = async payload => {
    setSaving(true);
    try {
      await api.post("/scheduled-broadcasts", payload);
      toast.success("Agendamento criado com sucesso.");
      setModalOpen(false);
      loadScheduledBroadcasts();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async scheduledBroadcastId => {
    try {
      await api.post(`/scheduled-broadcasts/${scheduledBroadcastId}/cancel`);
      toast.success("Agendamento cancelado.");
      loadScheduledBroadcasts();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async scheduledBroadcastId => {
    try {
      await api.delete(`/scheduled-broadcasts/${scheduledBroadcastId}`);
      toast.success("Agendamento removido.");
      setDeleteTarget(null);
      loadScheduledBroadcasts();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenDetails = async scheduledBroadcast => {
    setDetailsCampaign(scheduledBroadcast);
    setDetailsRecipients([]);
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const { data } = await api.get(`/scheduled-broadcasts/${scheduledBroadcast.id}`);
      setDetailsCampaign(data?.scheduledBroadcast || scheduledBroadcast);
      setDetailsRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
    } catch (err) {
      toastError(err);
      setDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <MainContainer>
      <ScheduledBroadcastModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreateScheduledBroadcast}
        loading={saving}
        whatsApps={whatsApps}
        tags={tags}
        contacts={contacts}
        queues={queues}
      />

      <ConfirmationModal
        title={
          deleteTarget
            ? `Deseja remover o agendamento "${deleteTarget.name}"?`
            : "Remover agendamento"
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget.id)}
      >
        Esta acao nao pode ser desfeita.
      </ConfirmationModal>

      <ScheduledBroadcastDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        campaign={detailsCampaign}
        recipients={detailsLoading ? [] : detailsRecipients}
        whatsApps={whatsApps}
        loading={detailsLoading}
      />

      <MainHeader>
        <Title description="Gerencie disparos comerciais, instâncias e progresso de envio.">
          Agendamento em Massa
        </Title>
        <MainHeaderButtonsWrapper>
          <IconButton
            className={classes.colorIcon}
            onClick={() => setModalOpen(true)}
            aria-label="Novo agendamento"
            size="large"
          >
            <Add />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">Nome</TableCell>
              <TableCell align="left">Instâncias</TableCell>
              <TableCell align="center">Agendado para</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Etiqueta</TableCell>
              <TableCell align="center">Fila</TableCell>
              <TableCell align="center">Progresso</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {scheduledBroadcasts.map(item => (
                <TableRow key={item.id}>
                  <TableCell align="left">{item.name}</TableCell>
                  <TableCell align="left">
                    {formatWhatsapps(item)}
                  </TableCell>
                  <TableCell align="center">
                    {formatDateTime(item.scheduledFor)}
                  </TableCell>
                  <TableCell align="center">
                    {formatCampaignStatus(item.status)}
                  </TableCell>
                  <TableCell align="center">{item.tag?.name || "-"}</TableCell>
                  <TableCell align="center">{item.queue?.name || "-"}</TableCell>
                  <TableCell align="center">
                    {item.sentCount}/{item.totalContacts} (falhas: {item.failedCount})
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDetails(item)}
                      title="Detalhes"
                    >
                      <Visibility />
                    </IconButton>
                    {(item.status === "pending" || item.status === "processing") && (
                      <IconButton
                        size="small"
                        onClick={() => handleCancel(item.id)}
                        title="Cancelar"
                      >
                        <Block />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleteTarget(item);
                        setConfirmModalOpen(true);
                      }}
                      title="Remover"
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(loading || contactsLoading || tagsLoading) && (
                <TableRowSkeleton columns={8} />
              )}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ScheduledBroadcasts;
